/* eslint-disable react-hooks/exhaustive-deps */
import { ReactNode, useEffect, useState, useRef } from "react";

import { createPortal } from "react-dom";

import { useModal } from "../../components";
import { UiModalModel } from "../../models";

import { AnimationPhase } from "./animations";
import {
    ModalContentContainer,
    ModalContent,
    CloseButton,
    Overlay
} from "./styled";

type Modal = {
    modal: {
        id: string;
        content: {
            transitionSpeed?: UiModalModel["content"]["transitionSpeed"];
            transitionType?: UiModalModel["content"]["transitionType"];
            modalSize?: UiModalModel["content"]["modalSize"];
            closeText?: UiModalModel["content"]["closeText"];
        };
    };
    isOpen: boolean;
    children: ReactNode;
    close: () => void;
};

export const Modal = ({ modal, isOpen, close, children }: Modal) => {
    const modalRoot = useModal(modal.id, isOpen);
    const [animation, setAnimation] = useState<AnimationPhase>("out");
    const modelContentRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (isOpen) {
            setAnimation("in");
            window.addEventListener("keydown", escPress);
            document.body.classList.add("prevent-scroll");
        }
        return () => {
            window.removeEventListener("keydown", escPress);
            document.body.classList.remove("prevent-scroll");
        };
    }, [isOpen]);

    const escPress = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
            closeModal();
        }
    };

    const closeModal = () => {
        setAnimation("out");
        document.body.classList.remove("prevent-scroll");
    };

    if (!modalRoot) {
        return null;
    }

    return createPortal(
        <Overlay
            data-testid={`ui-modal-${modal.id}-overlay`}
            animation={animation}
            transitionSpeed={modal.content.transitionSpeed}
            onTransitionEnd={(event) => {
                if (
                    animation === "out" &&
                    event.target === event.currentTarget
                ) {
                    close();
                }
            }}
        >
            <ModalContentContainer
                data-testid={`ui-modal-${modal.id}-container`}
                ref={modelContentRef}
                transitionSpeed={modal.content.transitionSpeed}
                transitionType={modal.content.transitionType}
                animation={animation}
                onClick={(event) => {
                    if (event.target === modelContentRef.current) {
                        closeModal();
                    }
                }}
            >
                <ModalContent
                    data-testid={`ui-modal-${modal.id}-content`}
                    modalSize={modal.content.modalSize}
                >
                    <CloseButton
                        data-testid={`ui-modal-${modal.id}-close-button`}
                        type="button"
                        title={modal.content.closeText}
                        onClick={closeModal}
                    >
                        <svg height="24" viewBox="0 0 24 24" width="24">
                            <path
                                d="m14.8523165 12 8.5569494 8.5569494c.7876455.7876455.7876455 2.0646711 0 2.8523165-.7876454.7876455-2.064671.7876455-2.8523165 0l-8.5569494-8.5569494-8.55694944 8.5569494c-.78764544.7876455-2.06467103.7876455-2.85231647 0-.78764545-.7876454-.78764545-2.064671 0-2.8523165l8.55694943-8.5569494-8.55694943-8.55694944c-.78764545-.78764544-.78764545-2.06467103 0-2.85231647.78764544-.78764545 2.06467103-.78764545 2.85231647 0l8.55694944 8.55694943 8.5569494-8.55694943c.7876455-.78764545 2.0646711-.78764545 2.8523165 0 .7876455.78764544.7876455 2.06467103 0 2.85231647z"
                                fill="#403294"
                            />
                        </svg>
                    </CloseButton>
                    {children}
                </ModalContent>
            </ModalContentContainer>
        </Overlay>,
        modalRoot
    );
};
